/**
 * authCleanup.js
 * Centralized utility to thoroughly purge all user-specific data from the browser
 * upon logout, session expiry, or when switching between accounts.
 */

import { dbService } from '@/service/db.service';

const PRESERVED_STORAGE_KEYS = new Set([
  'color-theme',
  'theme',
  'dark',
  'language',
  'triad_is_amazon_device',
  'triad_deployed_build_id',
  'triad_asset_reload',
  'user-song-config'
]);

export async function clearAllUserCaches() {
  console.log('[authCleanup] Purging all user caches...');

  // 1. Clear CacheStorage (Service Worker caches)
  if (typeof caches !== 'undefined') {
    try {
      const keys = await caches.keys();
      await Promise.all(
        keys.map((key) => {
          if (key.startsWith('triad-') || key.includes('triad')) {
            return caches.delete(key);
          }
          return Promise.resolve(false);
        })
      );
    } catch (e) {
      console.warn('[authCleanup] Error deleting CacheStorage:', e);
    }
  }

  // 2. Notify active Service Worker to invalidate caches
  try {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'INVALIDATE_API_CACHE' });
    }
  } catch (e) {
    console.warn('[authCleanup] Error messaging Service Worker:', e);
  }

  // 3. Clear In-Memory Cache (cacheInstance)
  try {
    if (typeof window !== 'undefined' && window.cacheInstance && typeof window.cacheInstance.clear === 'function') {
      window.cacheInstance.clear();
    }
  } catch (e) {
    console.warn('[authCleanup] Error clearing memory cache:', e);
  }

  // 4. Clear IndexedDB (triad_offline_db)
  try {
    if (dbService && typeof dbService.clear === 'function') {
      await dbService.clear();
    }
  } catch (e) {
    console.warn('[authCleanup] Error clearing IndexedDB:', e);
  }

  // 5. Clear localStorage user-specific items while preserving non-sensitive UI preferences
  if (typeof localStorage !== 'undefined') {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !PRESERVED_STORAGE_KEYS.has(key)) {
          if (
            key.startsWith('triad_offline_') ||
            key.startsWith('triad_pending_') ||
            key.startsWith('playlist_details_') ||
            key.startsWith('song::') ||
            key.includes('::song::') ||
            key.startsWith('triad_last_') ||
            key === 'triad_session_id' ||
            key === 'triad_dashboard_filter_tags' ||
            key === 'menu-list-favorites'
          ) {
            keysToRemove.push(key);
          }
        }
      }
      keysToRemove.forEach((k) => {
        try {
          localStorage.removeItem(k);
        } catch (_) {
          // ignore storage delete errors
        }
      });
    } catch (e) {
      console.warn('[authCleanup] Error clearing localStorage:', e);
    }
  }

  // 6. Clear sessionStorage
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem('triad_auth_in_progress');
    } catch (_) {
      // ignore storage delete errors
    }
  }

  console.log('[authCleanup] User caches successfully purged.');
}

export default clearAllUserCaches;
